import { useEffect, useState } from 'react';

import { useAppSelector } from '../../common/hooks';
import { selectSecurity } from '../../common/store/securitySlice';
import { getServiceCatalog } from '../services/ServiceCatalogService';
import { ServicePartner } from '../types';
import { usePreviewToken } from './usePreviewToken';

/**
 * Katalog se v rámci jedné návštěvy sekce načítá jednou (landing i detail sdílí tutéž odpověď).
 * Keš je klíčovaná domem, profilem, rolí i tokenem náhledu: BE vrací jiný katalog adminovi a
 * členovi, takže po odhlášení a přihlášení jiným účtem ve stejném domě nesmí zůstat katalog
 * předsedy (bug 10/2026); s náhledem obsahuje navíc nezveřejněného partnera.
 */
let cache: { key: string; partners: ServicePartner[] } | null = null;

export const invalidateServiceCatalog = (): void => {
  cache = null;
};

interface ServiceCatalogState {
  partners: ServicePartner[];
  isLoading: boolean;
  hasError: boolean;
  /** Token náhledu z URL – přenášet do odkazů uvnitř katalogu. */
  preview: string | null;
}

export const useServiceCatalog = (): ServiceCatalogState => {
  const security = useAppSelector(selectSecurity);
  const preview = usePreviewToken();
  const buildingId = security.ui.buildingId;
  const cacheKey = `${buildingId ?? ''}|${security.profile?.id ?? ''}|${security.ui.isAdmin ? 'admin' : 'member'}|${preview ?? ''}`;
  const cached = cache && cache.key === cacheKey ? cache.partners : null;

  const [partners, setPartners] = useState<ServicePartner[]>(cached ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(cached === null);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!buildingId) return undefined;
    if (cache && cache.key === cacheKey) {
      setPartners(cache.partners);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPartners([]);
    setIsLoading(true);
    setHasError(false);

    getServiceCatalog(preview)
      .then((items) => {
        if (cancelled) return;
        cache = { key: cacheKey, partners: items };
        setPartners(items);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buildingId, cacheKey, preview]);

  return { partners, isLoading, hasError, preview };
};
